/*
 * =========================================================
 * CHEMARRY PETFAMILY
 * Shopify Dawn 15.4.1
 * Filtros, búsqueda, variantes, carrusel y carrito dinámico
 * =========================================================
 */

(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-chem-petfamily]';
  const MOBILE_BREAKPOINT = 749;

  class ChemarryPetFamily {
    constructor(root) {
      this.root = root;

      if (!this.root || this.root.dataset.pfInitialized === 'true') {
        return;
      }

      this.root.dataset.pfInitialized = 'true';

      this.grid = this.root.querySelector('[data-pf-grid]');
      this.viewport = this.root.querySelector('[data-pf-viewport]');
      this.products = Array.from(
        this.root.querySelectorAll('[data-pf-product]')
      );

      this.filters = Array.from(
        this.root.querySelectorAll('[data-pf-filter]')
      );

      this.searchInput = this.root.querySelector('[data-pf-search]');
      this.countElement = this.root.querySelector('[data-pf-count]');
      this.emptyElement = this.root.querySelector('[data-pf-empty]');
      this.previousButton = this.root.querySelector('[data-pf-prev]');
      this.nextButton = this.root.querySelector('[data-pf-next]');

      this.statusElement = this.root.querySelector('[data-pf-status]');
      this.liveRegion = this.root.querySelector('[data-pf-live-region]');

      this.activeFilter = 'all';
      this.searchTerm = '';
      this.currentPage = 0;
      this.filteredProducts = [...this.products];
      this.statusTimer = null;

      this.desktopPageSize = this.getDesktopPageSize();

      this.handleResize = this.debounce(
        this.onResize.bind(this),
        150
      );

      this.bindEvents();
      this.prepareProducts();
      this.applyFilters();
    }

    /*
     * -------------------------------------------------------
     * CONFIGURACIÓN
     * -------------------------------------------------------
     */

    getDesktopPageSize() {
      const configuredSize = Number.parseInt(
        this.root.dataset.desktopPageSize || '',
        10
      );

      if (Number.isFinite(configuredSize) && configuredSize > 0) {
        return configuredSize;
      }

      return 4;
    }

    isMobile() {
      return window.matchMedia(
        `(max-width: ${MOBILE_BREAKPOINT}px)`
      ).matches;
    }

    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    }

    debounce(callback, delay = 150) {
      let timer;

      return (...args) => {
        window.clearTimeout(timer);

        timer = window.setTimeout(() => {
          callback(...args);
        }, delay);
      };
    }

    /*
     * -------------------------------------------------------
     * EVENTOS
     * -------------------------------------------------------
     */

    bindEvents() {
      this.filters.forEach((button) => {
        button.addEventListener('click', () => {
          this.setActiveFilter(button);
        });
      });

      if (this.searchInput) {
        this.searchInput.addEventListener(
          'input',
          this.debounce((event) => {
            this.searchTerm = this.normalizeText(
              event.target.value
            );

            this.currentPage = 0;
            this.applyFilters();
          }, 180)
        );

        this.searchInput.addEventListener('search', (event) => {
          this.searchTerm = this.normalizeText(
            event.target.value
          );

          this.currentPage = 0;
          this.applyFilters();
        });
      }

      if (this.previousButton) {
        this.previousButton.addEventListener('click', () => {
          this.goToPreviousPage();
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.goToNextPage();
        });
      }

      if (this.viewport) {
        this.viewport.addEventListener(
          'scroll',
          this.debounce(() => {
            if (this.isMobile()) {
              this.updateMobileNavigation();
            }
          }, 80),
          { passive: true }
        );
      }

      this.root.addEventListener('change', (event) => {
        const variantSelect = event.target.closest(
          '[data-pf-variant-select]'
        );

        if (variantSelect) {
          this.handleVariantChange(variantSelect);
        }
      });

      this.root.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-pf-form]');

        if (!form) {
          return;
        }

        event.preventDefault();
        this.addToCart(form);
      });

      window.addEventListener('resize', this.handleResize);
    }

    prepareProducts() {
      this.products.forEach((product) => {
        const variantSelect = product.querySelector(
          '[data-pf-variant-select]'
        );

        if (variantSelect) {
          this.handleVariantChange(variantSelect, false);
        }
      });
    }

    /*
     * -------------------------------------------------------
     * FILTROS Y BÚSQUEDA
     * -------------------------------------------------------
     */

    setActiveFilter(button) {
      const selectedFilter = this.normalizeText(
        button.dataset.filter || button.dataset.pfFilter || 'all'
      );

      this.activeFilter = selectedFilter || 'all';
      this.currentPage = 0;

      this.filters.forEach((filterButton) => {
        const buttonFilter = this.normalizeText(
          filterButton.dataset.filter ||
            filterButton.dataset.pfFilter ||
            'all'
        );

        const isActive = buttonFilter === this.activeFilter;

        filterButton.classList.toggle('is-active', isActive);
        filterButton.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );
      });

      this.applyFilters();
    }

    productMatchesFilter(product) {
      if (this.activeFilter === 'all') {
        return true;
      }

      const categories = this.normalizeText(
        product.dataset.categories || ''
      )
        .split('|')
        .map((category) => category.trim())
        .filter(Boolean);

      return categories.includes(this.activeFilter);
    }

    productMatchesSearch(product) {
      if (!this.searchTerm) {
        return true;
      }

      const searchableText = this.normalizeText(
        product.dataset.search || product.textContent || ''
      );

      return searchableText.includes(this.searchTerm);
    }

    applyFilters() {
      this.filteredProducts = this.products.filter((product) => {
        return (
          this.productMatchesFilter(product) &&
          this.productMatchesSearch(product)
        );
      });

      const filteredSet = new Set(this.filteredProducts);

      this.products.forEach((product) => {
        const isFiltered = !filteredSet.has(product);

        product.classList.toggle('is-filtered', isFiltered);
        product.setAttribute(
          'aria-hidden',
          isFiltered ? 'true' : 'false'
        );
      });

      const totalPages = this.getTotalPages();

      if (this.currentPage >= totalPages) {
        this.currentPage = Math.max(0, totalPages - 1);
      }

      this.renderProducts();
      this.updateCount();
      this.updateEmptyState();
      this.updateNavigation();
      this.resetViewportPosition();
    }

    /*
     * -------------------------------------------------------
     * PAGINACIÓN Y CARRUSEL
     * -------------------------------------------------------
     */

    getTotalPages() {
      if (this.isMobile()) {
        return this.filteredProducts.length > 0 ? 1 : 0;
      }

      return Math.ceil(
        this.filteredProducts.length / this.desktopPageSize
      );
    }

    renderProducts() {
      if (this.isMobile()) {
        this.filteredProducts.forEach((product) => {
          product.classList.add('is-page-visible');
          product.removeAttribute('tabindex');
        });

        return;
      }

      const start = this.currentPage * this.desktopPageSize;
      const end = start + this.desktopPageSize;

      this.filteredProducts.forEach((product, index) => {
        const isVisible = index >= start && index < end;

        product.classList.toggle(
          'is-page-visible',
          isVisible
        );

        product.setAttribute(
          'aria-hidden',
          isVisible ? 'false' : 'true'
        );
      });
    }

    goToPreviousPage() {
      if (this.isMobile()) {
        this.scrollMobile(-1);
        return;
      }

      if (this.currentPage <= 0) {
        return;
      }

      this.currentPage -= 1;
      this.renderProducts();
      this.updateNavigation();
      this.focusFirstVisibleProduct();
    }

    goToNextPage() {
      if (this.isMobile()) {
        this.scrollMobile(1);
        return;
      }

      const totalPages = this.getTotalPages();

      if (this.currentPage >= totalPages - 1) {
        return;
      }

      this.currentPage += 1;
      this.renderProducts();
      this.updateNavigation();
      this.focusFirstVisibleProduct();
    }

    scrollMobile(direction) {
      if (!this.viewport) {
        return;
      }

      const visibleProduct = this.filteredProducts.find(
        (product) => !product.classList.contains('is-filtered')
      );

      const cardWidth =
        visibleProduct?.getBoundingClientRect().width || 280;

      const styles = window.getComputedStyle(this.grid);
      const gap =
        Number.parseFloat(styles.columnGap || styles.gap) || 12;

      this.viewport.scrollBy({
        left: direction * (cardWidth + gap),
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth'
      });
    }

    resetViewportPosition() {
      if (!this.viewport) {
        return;
      }

      this.viewport.scrollTo({
        left: 0,
        behavior: 'auto'
      });
    }

    focusFirstVisibleProduct() {
      const firstVisibleProduct = this.filteredProducts.find(
        (product) =>
          product.classList.contains('is-page-visible')
      );

      const focusTarget = firstVisibleProduct?.querySelector(
        'a, button, select, input'
      );

      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
      }
    }

    updateNavigation() {
      if (!this.previousButton || !this.nextButton) {
        return;
      }

      if (this.isMobile()) {
        this.updateMobileNavigation();
        return;
      }

      const totalPages = this.getTotalPages();
      const hasMultiplePages = totalPages > 1;

      this.previousButton.hidden = !hasMultiplePages;
      this.nextButton.hidden = !hasMultiplePages;

      this.setButtonDisabled(
        this.previousButton,
        this.currentPage <= 0
      );

      this.setButtonDisabled(
        this.nextButton,
        this.currentPage >= totalPages - 1
      );
    }

    updateMobileNavigation() {
      if (
        !this.viewport ||
        !this.previousButton ||
        !this.nextButton
      ) {
        return;
      }

      const hasOverflow =
        this.viewport.scrollWidth >
        this.viewport.clientWidth + 2;

      this.previousButton.hidden = !hasOverflow;
      this.nextButton.hidden = !hasOverflow;

      const maxScroll =
        this.viewport.scrollWidth -
        this.viewport.clientWidth;

      this.setButtonDisabled(
        this.previousButton,
        this.viewport.scrollLeft <= 2
      );

      this.setButtonDisabled(
        this.nextButton,
        this.viewport.scrollLeft >= maxScroll - 2
      );
    }

    setButtonDisabled(button, disabled) {
      button.disabled = disabled;
      button.setAttribute(
        'aria-disabled',
        disabled ? 'true' : 'false'
      );
    }

    updateCount() {
      if (!this.countElement) {
        return;
      }

      const count = this.filteredProducts.length;

      this.countElement.textContent =
        count === 1 ? '1 producto' : `${count} productos`;

      if (this.liveRegion) {
        this.liveRegion.textContent =
          count === 1
            ? 'Se encontró 1 producto'
            : `Se encontraron ${count} productos`;
      }
    }

    updateEmptyState() {
      if (!this.emptyElement) {
        return;
      }

      const isEmpty = this.filteredProducts.length === 0;

      this.emptyElement.classList.toggle(
        'is-visible',
        isEmpty
      );

      this.emptyElement.hidden = !isEmpty;
    }

    onResize() {
      this.currentPage = 0;
      this.renderProducts();
      this.updateNavigation();
      this.resetViewportPosition();
    }

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }

    /*
     * -------------------------------------------------------
     * VARIANTES
     * -------------------------------------------------------
     */

    handleVariantChange(select, announce = true) {
      const product = select.closest('[data-pf-product]');

      if (!product) {
        return;
      }

      const option = select.options[select.selectedIndex];

      if (!option) {
        return;
      }

      const form = product.querySelector('[data-pf-form]');
      const variantInput = form?.querySelector('input[name="id"]');
      const currentPrice = product.querySelector(
        '[data-pf-current-price]'
      );
      const comparePrice = product.querySelector(
        '[data-pf-compare-price]'
      );
      const savingElement = product.querySelector(
        '[data-pf-saving]'
      );
      const availability = product.querySelector(
        '[data-pf-availability]'
      );
      const addButton = product.querySelector('[data-pf-add]');
      const buttonText = addButton?.querySelector(
        '[data-pf-button-text]'
      );
      const productLink = product.querySelector(
        '[data-pf-product-link]'
      );
      const productImage = product.querySelector(
        '[data-pf-product-image]'
      );

      const variantId = option.value;
      const available =
        option.dataset.available !== 'false' &&
        !option.disabled;

      const price = this.parseMoneyValue(
        option.dataset.price
      );

      const compareAtPrice = this.parseMoneyValue(
        option.dataset.comparePrice
      );

      if (variantInput) {
        variantInput.value = variantId;
      }

      if (currentPrice) {
        currentPrice.textContent =
          option.dataset.priceFormatted ||
          this.formatMoney(price);

        currentPrice.classList.toggle(
          'chem-petfamily__price-current--sale',
          compareAtPrice > price
        );
      }

      if (comparePrice) {
        if (compareAtPrice > price) {
          comparePrice.textContent =
            option.dataset.comparePriceFormatted ||
            this.formatMoney(compareAtPrice);

          comparePrice.hidden = false;
        } else {
          comparePrice.textContent = '';
          comparePrice.hidden = true;
        }
      }

      if (savingElement) {
        const saving = compareAtPrice - price;

        if (saving > 0) {
          savingElement.textContent = `Ahorras ${this.formatMoney(
            saving
          )}`;

          savingElement.hidden = false;
        } else {
          savingElement.textContent = '';
          savingElement.hidden = true;
        }
      }

      if (availability) {
        availability.classList.toggle(
          'chem-petfamily__availability--soldout',
          !available
        );

        availability.textContent = available
          ? 'Disponible'
          : 'Agotado';
      }

      if (addButton) {
        addButton.disabled = !available;
        addButton.setAttribute(
          'aria-disabled',
          available ? 'false' : 'true'
        );
      }

      if (buttonText) {
        buttonText.textContent = available
          ? 'Agregar al carrito'
          : 'Agotado';
      }

      const variantUrl = option.dataset.url;

      if (productLink && variantUrl) {
        productLink.href = variantUrl;
      }

      const variantImage = option.dataset.image;

      if (productImage && variantImage) {
        productImage.src = variantImage;

        if (option.dataset.imageSrcset) {
          productImage.srcset =
            option.dataset.imageSrcset;
        } else {
          productImage.removeAttribute('srcset');
        }

        if (option.dataset.imageAlt) {
          productImage.alt = option.dataset.imageAlt;
        }
      }

      if (announce && this.liveRegion) {
        this.liveRegion.textContent = available
          ? `Variante seleccionada: ${option.textContent.trim()}. Disponible.`
          : `Variante seleccionada: ${option.textContent.trim()}. Agotada.`;
      }
    }

    parseMoneyValue(value) {
      const parsedValue = Number.parseInt(value || '0', 10);

      return Number.isFinite(parsedValue) ? parsedValue : 0;
    }

    formatMoney(cents) {
      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function'
      ) {
        try {
          return window.Shopify.formatMoney(
            cents,
            this.root.dataset.moneyFormat || '${{amount_no_decimals}}'
          );
        } catch (error) {
          /* Continúa con el formato local. */
        }
      }

      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0
      }).format(cents / 100);
    }

    /*
     * -------------------------------------------------------
     * AGREGAR AL CARRITO
     * -------------------------------------------------------
     */

    async addToCart(form) {
      const button = form.querySelector('[data-pf-add]');
      const buttonText = button?.querySelector(
        '[data-pf-button-text]'
      );

      if (!button || button.disabled) {
        return;
      }

      const variantInput = form.querySelector(
        'input[name="id"]'
      );

      if (!variantInput?.value) {
        this.showStatus(
          'Selecciona una variante antes de continuar.',
          'error'
        );

        return;
      }

      const originalButtonText =
        buttonText?.textContent || 'Agregar al carrito';

      this.setLoadingState(button, true);

      if (buttonText) {
        buttonText.textContent = 'Agregando…';
      }

      try {
        const formData = new FormData(form);
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector(
          'cart-notification'
        );

        const sections = [];

        if (cartDrawer) {
          sections.push('cart-drawer');
        }

        if (cartNotification) {
          sections.push('cart-notification');
        }

        sections.push('cart-icon-bubble');

        formData.set('sections', [...new Set(sections)].join(','));
        formData.set(
          'sections_url',
          window.location.pathname
        );

        const response = await fetch(
          window.routes?.cart_add_url || '/cart/add.js',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          }
        );

        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
              result.message ||
              'No pudimos agregar el producto.'
          );
        }

        this.renderCart(result);
        this.showStatus(
          'Producto agregado al carrito.',
          'success'
        );

        if (buttonText) {
          buttonText.textContent = 'Agregado';
        }

        window.setTimeout(() => {
          if (buttonText && document.contains(buttonText)) {
            buttonText.textContent = originalButtonText;
          }
        }, 1600);

        this.dispatchCartEvents(result);
      } catch (error) {
        const message =
          error?.message ||
          'No pudimos agregar el producto. Inténtalo nuevamente.';

        this.showStatus(message, 'error');

        if (buttonText) {
          buttonText.textContent = 'Intentar nuevamente';
        }

        window.setTimeout(() => {
          if (buttonText && document.contains(buttonText)) {
            buttonText.textContent = originalButtonText;
          }
        }, 2200);
      } finally {
        this.setLoadingState(button, false);
      }
    }

    setLoadingState(button, loading) {
      button.classList.toggle('is-loading', loading);
      button.disabled = loading;
      button.setAttribute(
        'aria-busy',
        loading ? 'true' : 'false'
      );
    }

    renderCart(result) {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector(
        'cart-notification'
      );

      if (
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function'
      ) {
        cartDrawer.renderContents(result);

        window.requestAnimationFrame(() => {
          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            cartDrawer.classList.add(
              'animate',
              'active'
            );
          }
        });

        return;
      }

      if (
        cartNotification &&
        typeof cartNotification.renderContents === 'function'
      ) {
        cartNotification.renderContents(result);
        return;
      }

      this.replaceReturnedSections(result.sections);

      const fallbackDrawer = document.querySelector('cart-drawer');

      if (fallbackDrawer) {
        if (typeof fallbackDrawer.open === 'function') {
          fallbackDrawer.open();
        } else {
          fallbackDrawer.classList.add(
            'animate',
            'active'
          );
        }
      }
    }

    replaceReturnedSections(sections) {
      if (!sections) {
        return;
      }

      Object.entries(sections).forEach(
        ([sectionId, sectionHtml]) => {
          if (!sectionHtml) {
            return;
          }

          const parser = new DOMParser();
          const parsedDocument = parser.parseFromString(
            sectionHtml,
            'text/html'
          );

          if (sectionId === 'cart-icon-bubble') {
            const currentBubble = document.getElementById(
              'cart-icon-bubble'
            );

            const newBubble = parsedDocument.getElementById(
              'cart-icon-bubble'
            );

            if (currentBubble && newBubble) {
              currentBubble.innerHTML =
                newBubble.innerHTML;
            }

            return;
          }

          const currentSection = document.getElementById(
            `shopify-section-${sectionId}`
          );

          const newSection = parsedDocument.getElementById(
            `shopify-section-${sectionId}`
          );

          if (currentSection && newSection) {
            currentSection.innerHTML =
              newSection.innerHTML;
          }
        }
      );
    }

    dispatchCartEvents(result) {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            source: 'chemarry-petfamily',
            cart: result
          }
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            source: 'chemarry-petfamily'
          }
        })
      );
    }

    /*
     * -------------------------------------------------------
     * MENSAJES
     * -------------------------------------------------------
     */

    showStatus(message, type = 'success') {
      if (!this.statusElement) {
        return;
      }

      window.clearTimeout(this.statusTimer);

      this.statusElement.textContent = message;
      this.statusElement.classList.remove(
        'chem-petfamily__status--success',
        'chem-petfamily__status--error'
      );

      this.statusElement.classList.add(
        'is-visible',
        type === 'error'
          ? 'chem-petfamily__status--error'
          : 'chem-petfamily__status--success'
      );

      this.statusElement.setAttribute(
        'role',
        type === 'error' ? 'alert' : 'status'
      );

      this.statusTimer = window.setTimeout(() => {
        this.statusElement.classList.remove('is-visible');
      }, type === 'error' ? 5000 : 3200);
    }

    destroy() {
      window.removeEventListener(
        'resize',
        this.handleResize
      );

      this.root.dataset.pfInitialized = 'false';
    }
  }

  /*
   * ---------------------------------------------------------
   * INICIALIZACIÓN
   * ---------------------------------------------------------
   */

  const instances = new WeakMap();

  function initializePetFamily(scope = document) {
    const roots = [];

    if (
      scope instanceof Element &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    }

    roots.forEach((root) => {
      if (
        root.dataset.pfInitialized === 'true' &&
        instances.has(root)
      ) {
        return;
      }

      const instance = new ChemarryPetFamily(root);
      instances.set(root, instance);
    });
  }

  function initializeWhenReady() {
    initializePetFamily(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeWhenReady,
      { once: true }
    );
  } else {
    initializeWhenReady();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializePetFamily(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const roots = [];

      if (
        event.target instanceof Element &&
        event.target.matches(ROOT_SELECTOR)
      ) {
        roots.push(event.target);
      }

      if (event.target.querySelectorAll) {
        roots.push(
          ...event.target.querySelectorAll(ROOT_SELECTOR)
        );
      }

      roots.forEach((root) => {
        const instance = instances.get(root);

        if (instance) {
          instance.destroy();
          instances.delete(root);
        }
      });
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const product = event.target.closest(
        '[data-pf-product]'
      );

      if (!product) {
        return;
      }

      product.scrollIntoView({
        behavior: window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
          ? 'auto'
          : 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  );
})();